/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import * as React from "react";
import { DeviceHelper } from "@nf/common";
import cx from "classnames";
import styles from "./Input.module.scss";

export interface IInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  inputClassName?: string;
  suffix?: React.ReactNode;
  checkIfAutoCompleted?: () => void;
  trimValue?: boolean;
}

const supportsAutofill = (() => {
  try {
    document.createElement("input").matches(":autofill");
    return true;
  } catch {
    return false;
  }
})();

export const Input = React.forwardRef<HTMLInputElement, IInputProps>(
  (
    {
      style,
      className,
      inputClassName,
      value,
      suffix,
      type = "text",
      trimValue,
      checkIfAutoCompleted,
      onChange,
      ...rest
    },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current!);

    React.useEffect(() => {
      if (!checkIfAutoCompleted || DeviceHelper.isFirefox()) return;

      const timeout = window.setTimeout(() => {
        const input = inputRef.current!;
        if (
          supportsAutofill ? input.matches(":autofill") : input.value !== ""
        ) {
          checkIfAutoCompleted();
        }
      }, 400);

      return () => {
        clearTimeout(timeout);
      };
    }, [checkIfAutoCompleted]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (trimValue) {
        e.target.value = e.target.value.trim();
      }

      onChange?.(e);
    };

    const handleSuffixMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      // Prevent focused state lost
      e.preventDefault();
    };

    const handleSuffixMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      inputRef.current?.focus();
    };

    return (
      <span style={style} className={cx(styles.container, className)}>
        <input
          {...rest}
          ref={inputRef}
          value={value ?? ""}
          className={cx(styles.input, inputClassName)}
          type={type}
          onChange={handleChange}
        />
        {suffix && (
          <div
            className={styles.suffix}
            onMouseDown={handleSuffixMouseDown}
            onMouseUp={handleSuffixMouseUp}
          >
            {suffix}
          </div>
        )}
      </span>
    );
  }
);

export default Input;
